'use client';

import { useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { MapPin } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { useSafeNavigation } from '@/hooks/useSafeNavigation';
import { useFestaEntryStore } from '@/stores/useFestaEntryStore';
import {
    FestaEntryFestaEntryTypeItem,
    FestaEntryItem,
    FestaEntryLocationItem,
    FestaEntryScheduleItem,
    UserFestaEntryDraftData,
    UserFestaEntryFormData,
} from '@/types/festaEntries';
import ErrorBox from '@/components/ErrorBox';
import ImageAdaptive from '@/components/ImageAdaptive';
import LabelBadge from '@/components/LabelBadge';

export interface FestaEntryFormProps {
    refreshAuth: () => Promise<boolean>;
    formData: UserFestaEntryFormData;
    setFormData: React.Dispatch<React.SetStateAction<UserFestaEntryFormData>>;
    onNext: () => void;
    itemStart: number;
    festaEntryList: FestaEntryItem[];
    festaEntryTypes: FestaEntryFestaEntryTypeItem[];
    festaEntryScheduleList: FestaEntryScheduleItem[];
    festaEntryLocationList: FestaEntryLocationItem[];
    onChangeSelectedFestaEntryId: (id: number) => void;
    isProcessing: boolean;
}
export default function FestaEntryForm({
    refreshAuth,
    formData,
    setFormData,
    onNext,
    itemStart,
    festaEntryList,
    festaEntryTypes,
    festaEntryScheduleList,
    festaEntryLocationList,
    onChangeSelectedFestaEntryId,
    isProcessing,
}: FestaEntryFormProps) {
    const { safePush } = useSafeNavigation();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const currentPath = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    const validationRef = useRef<HTMLDivElement>(null);
    const { errorMessage: submitErrorMessage } = useHandleError(validationRef);

    const hasFestaSelected = Number(formData.festa_entry_id) > 0;
    const isFirstSectionCompleted = hasFestaSelected && formData.location_id > 0 && formData.schedule_group_id > 0;
    const isAllSelected =
        formData.festa_entry_id > 0 &&
        formData.schedule_group_id > 0 &&
        formData.location_id > 0 &&
        formData.festa_entry_type_cd != '';
    const selectedType = festaEntryTypes.find((t) => t.cd === formData.festa_entry_type_cd);
    const isLoginRequired = !!selectedType?.is_login_required;

    const buildAddress = (item: FestaEntryLocationItem) => {
        const pieces = [
            item.zip ? `〒${item.zip}` : '',
            item.prefecture_name ?? '',
            item.address_1 ?? '',
            item.address_2 ?? '',
        ].filter(Boolean);
        return pieces.join(' ');
    };

    const patchDraft = useFestaEntryStore((s) => s.patchDraft);

    const toDraft = (fd: UserFestaEntryFormData): UserFestaEntryDraftData => ({
        festa_entry_id: fd.festa_entry_id,
        festa_entry_name: fd.festa_entry_name ?? '',
        festa_entry_contact_email: fd.festa_entry_contact_email ?? '',
        festa_entry_contact_name: fd.festa_entry_contact_name ?? '',
        schedule_group_id: fd.schedule_group_id,
        schedule_labels: fd.schedule_labels ?? [],
        location_id: fd.location_id,
        location_name: fd.location_name ?? '',
        festa_entry_type_id: fd.festa_entry_type_id,
        festa_entry_type_cd: fd.festa_entry_type_cd,
        festa_entry_type_name: fd.festa_entry_type_name ?? '',
        festa_entry_type_is_login_required: fd.festa_entry_type_is_login_required ?? 0,
        eyecatch_public_url: fd.eyecatch_public_url ?? '',
    });

    const saveDraft = (next?: Partial<UserFestaEntryFormData>) => {
        const merged = { ...formData, ...(next || {}) };
        patchDraft(toDraft(merged));
    };

    const handleFestaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const id = Number(e.target.value) || 0;
        const hit = festaEntryList.find((f) => f.festa_entry_id === id);
        const next: UserFestaEntryFormData = {
            ...formData,
            festa_entry_id: id,
            festa_entry_name: hit?.name ?? '',
            festa_entry_contact_email: hit?.contact_email ?? '',
            festa_entry_contact_name: hit?.contact_name ?? '',
            schedule_group_id: 0,
            schedule_labels: [],
            location_id: 0,
            location_name: '',
            festa_entry_type_id: 0,
            festa_entry_type_cd: '',
            festa_entry_type_name: '',
            festa_entry_type_is_login_required: 0,
            eyecatch_public_url: hit?.eyecatch_public_url ?? '',
        };
        setFormData(next);
        onChangeSelectedFestaEntryId?.(id);
    };

    const handleScheduleChange = (schedule_group_id: number) => {
        const hit = festaEntryScheduleList.find((s) => s.schedule_group_id === schedule_group_id);
        const next: UserFestaEntryFormData = {
            ...formData,
            schedule_group_id,
            schedule_labels: hit?.schedule_labels ?? [],
        };
        setFormData(next);
    };

    const handleLocationChange = (id: number) => {
        const hit = festaEntryLocationList.find((l) => l.id === id);
        const next = {
            ...formData,
            location_id: id,
            location_name: hit?.name ?? '',
        };
        setFormData(next);
    };

    const handleTypeChange = (cd: string) => {
        const hit = festaEntryTypes.find((t) => t.cd === cd);
        const next = {
            ...formData,
            festa_entry_type_cd: cd,
            festa_entry_type_name: hit?.name ?? '',
            festa_entry_type_is_login_required: hit?.is_login_required ?? 0,
        };
        setFormData(next);
    };

    const goLogin = async () => {
        const isAuthenticatedServer = await refreshAuth();
        if (!isAllSelected || isProcessing) return;
        if (isAuthenticatedServer) {
            onNext();
        } else {
            saveDraft();
            safePush(`/auth/login?redirect=${encodeURIComponent(currentPath)}`);
        }
    };

    const goSignup = async () => {
        const isAuthenticatedServer = await refreshAuth();
        if (!isAllSelected || isProcessing) return;
        saveDraft();
        if (isAuthenticatedServer) {
            onNext();
        } else {
            saveDraft();
            safePush(`/auth/signup?redirect=${encodeURIComponent(currentPath)}`);
        }
    };

    return (
        <>
            <div ref={validationRef} className="flex flex-col justify-start items-center w-full xl:w-[60%] pb-12">
                <div className="flex flex-col justify-start items-center pt-4 pb-1 w-full">
                    <div className="flex w-[80%] lg:w-[800px]">
                        <ErrorBox errors={submitErrorMessage?.errors} />
                    </div>
                    <div className="flex justify-start w-[80%] lg:w-[800px]">
                        <div className="flex flex-col justify-center items-center">
                            <div className="flex items-center pb-2">
                                <ImageAdaptive
                                    src={`/images/common/item${itemStart++}.svg`}
                                    baseWidth={50}
                                    breakpoints={{ md: 60 }}
                                />
                            </div>
                            <div className="flex-grow border-l-2 border-color-base-2 w-0"></div>
                        </div>
                        <div className="px-2"></div>
                        <div className="flex flex-col justify-start items-start font-bold text-base xl:text-xl w-full">
                            <div className="flex items-center pt-2 md:pt-3">
                                <div className="flex text-color-base-2">参加するフェスタを選択してください</div>
                                <LabelBadge type="required" />
                            </div>
                            <div className="flex flex-col w-full pt-8 pb-4">
                                <div className="flex py-1">
                                    <div className="font-medium text-sm text-color-base pr-2">フェスタ</div>
                                </div>
                                <select
                                    className="select-form-w p-4"
                                    value={formData.festa_entry_id ? String(formData.festa_entry_id) : ''}
                                    onChange={handleFestaChange}
                                >
                                    <option value="">選択してください</option>
                                    {festaEntryList.map((festa) => (
                                        <option key={festa.festa_entry_id} value={festa.festa_entry_id}>
                                            {festa.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {hasFestaSelected && festaEntryLocationList.length > 0 && (
                                <div className="flex flex-col w-full pb-4">
                                    <div className="flex py-1">
                                        <div className="font-medium text-sm text-color-base pr-2">開催地</div>
                                    </div>
                                    <div className="w-full text-color-base-1-deep">
                                        {festaEntryLocationList.map((location, index) => {
                                            const isOnline = location.is_online === 1;
                                            const address = buildAddress(location);
                                            const inputId = `select_festa_entry_location_${index}`;
                                            return (
                                                <div
                                                    key={location.id}
                                                    className="md:grid md:grid-cols-[auto_minmax(0,1fr)_auto] md:items-center md:gap-3"
                                                >
                                                    <div className="flex flex-col md:flex-row md:items-center md:col-span-2">
                                                        <div className="flex items-center gap-2 pb-2 md:pb-0">
                                                            <label
                                                                htmlFor={inputId}
                                                                className="flex items-center gap-2 cursor-pointer"
                                                            >
                                                                <div className="flex justify-center items-center w-12">
                                                                    <div className="relative flex items-center rounded-full p-2">
                                                                        <input
                                                                            type="radio"
                                                                            name="festalocation"
                                                                            id={inputId}
                                                                            value={location.id}
                                                                            checked={
                                                                                formData.location_id === location.id
                                                                            }
                                                                            onChange={() =>
                                                                                handleLocationChange(location.id)
                                                                            }
                                                                            className="before:content[''] peer relative h-8 w-8 appearance-none
                                                                            border-2 border-gray-300 text-color-dashboard-signature-achievement-selected transition-all
                                                                            before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12
                                                                            before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500
                                                                            before:opacity-0 before:transition-opacity
                                                                            checked:border-color-dashboard-signature-achievement-selected
                                                                            checked:before:bg-color-dashboard-signature-achievement-selected
                                                                            hover:before:opacity-10 cursor-pointer rounded-full"
                                                                        />
                                                                        <div className="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 opacity-0 transition-opacity peer-checked:opacity-100">
                                                                            <ImageAdaptive
                                                                                src="/images/common/radio-select.svg"
                                                                                baseWidth={18}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="flex flex-col items-start">
                                                                    <span
                                                                        className={[
                                                                            'inline-flex md:hidden items-center rounded-full px-3 py-1 text-xs font-medium',
                                                                            'whitespace-nowrap shrink-0',
                                                                            isOnline
                                                                                ? 'bg-emerald-100 text-emerald-700'
                                                                                : 'bg-slate-100 text-slate-700',
                                                                        ].join(' ')}
                                                                    >
                                                                        {isOnline ? 'オンライン' : 'リアル会場'}
                                                                    </span>
                                                                    <span className="font-normal text-sm md:text-base text-color-base leading-snug break-words pt-[1px] md:pt-0">
                                                                        {location.name}
                                                                    </span>
                                                                </div>
                                                            </label>
                                                            {!isOnline && (
                                                                <a
                                                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                                        address,
                                                                    )}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-slate-600 hover:text-slate-800 flex items-center pt-6 md:pt-0"
                                                                >
                                                                    <MapPin size={18} className="text-slate-600" />
                                                                </a>
                                                            )}
                                                        </div>
                                                        <div className="hidden md:flex items-center ml-12 md:ml-2 mt-1 md:mt-0">
                                                            <span
                                                                className={[
                                                                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-medium',
                                                                    'whitespace-nowrap shrink-0',
                                                                    isOnline
                                                                        ? 'bg-emerald-100 text-emerald-700'
                                                                        : 'bg-slate-100 text-slate-700',
                                                                ].join(' ')}
                                                            >
                                                                {isOnline ? 'オンライン' : 'リアル会場'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            {hasFestaSelected && festaEntryScheduleList.length > 0 && (
                                <div className="flex flex-col w-full pb-4">
                                    <div className="flex py-1">
                                        <div className="font-medium text-sm text-color-base pr-2">日程</div>
                                    </div>
                                    <div className="w-full text-color-base-1-deep">
                                        {festaEntryScheduleList.map((schedule, index) => {
                                            const inputId = `select_festa_entry_schedules_${index}`;
                                            return (
                                                <label
                                                    key={schedule.schedule_group_id}
                                                    htmlFor={inputId}
                                                    className="flex justify-start w-full cursor-pointer"
                                                >
                                                    <div className="flex justify-center items-start w-12 lg:w-12 h-full">
                                                        <div className="relative flex items-center rounded-full p-2">
                                                            <input
                                                                type="radio"
                                                                name="festaschedule"
                                                                id={inputId}
                                                                value={schedule.schedule_group_id}
                                                                checked={
                                                                    formData.schedule_group_id ===
                                                                    schedule.schedule_group_id
                                                                }
                                                                onChange={() =>
                                                                    handleScheduleChange(schedule.schedule_group_id)
                                                                }
                                                                className="before:content[''] peer relative h-8 w-8 appearance-none
                                                                border-2 border-gray-300 text-color-dashboard-signature-achievement-selected transition-all
                                                                before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12
                                                                before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500
                                                                before:opacity-0 before:transition-opacity
                                                                checked:border-color-dashboard-signature-achievement-selected
                                                                checked:before:bg-color-dashboard-signature-achievement-selected
                                                                hover:before:opacity-10 cursor-pointer rounded-full"
                                                            />
                                                            <div className="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 opacity-0 transition-opacity peer-checked:opacity-100">
                                                                <ImageAdaptive
                                                                    src="/images/common/radio-select.svg"
                                                                    baseWidth={18}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col justify-center">
                                                        {schedule.schedule_labels.map((s) => (
                                                            <div
                                                                key={`select_festa_entry_schedule_${s}`}
                                                                className="flex items-center font-normal text-sm md:text-base text-color-base pl-2"
                                                            >
                                                                {s}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <div className="pt-12"></div>
                        </div>
                    </div>

                    <div className="flex flex-col justify-start items-center pt-1 pb-1 w-full">
                        <div className="flex justify-start w-[80%] lg:w-[800px]">
                            <div className="flex flex-col justify-center items-center">
                                <div className="flex items-center pb-2">
                                    <ImageAdaptive
                                        src={`/images/common/item${itemStart++}.svg`}
                                        baseWidth={50}
                                        breakpoints={{ md: 60 }}
                                    />
                                </div>
                                <div className="flex-grow border-l-2 border-color-base-2 w-0"></div>
                            </div>
                            <div className="px-2"></div>
                            <div className="flex flex-col justify-start items-start font-bold text-base xl:text-xl w-full">
                                <div className="flex items-center pt-2 md:pt-3">
                                    <div className="flex text-color-base-2">参加方法を選択してください</div>
                                    <LabelBadge type="required" />
                                </div>
                                {isFirstSectionCompleted && festaEntryTypes.length > 0 && (
                                    <div className="flex flex-col w-full pt-8 pb-12">
                                        <div className="bg-color-white-base text-color-base-1-deep">
                                            {festaEntryTypes.map((type) => {
                                                const inputId = `select_festa_entry_type_${type.cd}`;
                                                return (
                                                    <label
                                                        key={type.cd}
                                                        htmlFor={inputId}
                                                        className="flex justify-start w-full cursor-pointer border-0 outline-none"
                                                        data-ripple-dark="true"
                                                    >
                                                        <div className="flex justify-center items-start w-12 lg:w-12 h-full">
                                                            <div className="relative flex items-center rounded-full p-2">
                                                                <input
                                                                    type="radio"
                                                                    id={inputId}
                                                                    name="festaType"
                                                                    value={type.cd}
                                                                    checked={formData.festa_entry_type_cd === type.cd}
                                                                    onChange={() => handleTypeChange(type.cd)}
                                                                    className="before:content[''] peer relative h-8 w-8 appearance-none
                                                                    border-2 border-gray-300 text-color-dashboard-signature-achievement-selected transition-all
                                                                    before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12
                                                                    before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500
                                                                    before:opacity-0 before:transition-opacity
                                                                    checked:border-color-dashboard-signature-achievement-selected
                                                                    checked:before:bg-color-dashboard-signature-achievement-selected
                                                                    hover:before:opacity-10 cursor-pointer rounded-full"
                                                                />
                                                                <div className="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 text-pink-500 opacity-0 transition-opacity peer-checked:opacity-100">
                                                                    <ImageAdaptive
                                                                        src="/images/common/radio-select.svg"
                                                                        baseWidth={18}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex flex-col justify-center">
                                                            <div className="flex items-center font-normal text-sm md:text-base text-color-base pl-2">
                                                                {type.name}
                                                            </div>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col-reverse md:flex-row justify-center items-center w-full pb-20 gap-4">
                <div className="flex flex-grow"></div>
                {!isLoginRequired ? (
                    <div className="flex justify-center font-medium text-sm w-full md:w-[40%] xl:w-[18%] px-2 lx:px-4">
                        <button
                            disabled={!isAllSelected || isProcessing}
                            onClick={onNext}
                            className={`m-2 py-4 px-12 bg-color-base-2 font-notojp font-bold text-color-white-base rounded-3xl shadow-right-bottom hover:opacity-90 whitespace-nowrap
                            ${
                                !isAllSelected || isProcessing
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-color-base-2 hover:opacity-90'
                            }`}
                        >
                            次へ
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex justify-center font-medium text-sm w-full md:w-[40%] xl:w-[18%] px-2 lx:px-4">
                            <button
                                disabled={!isAllSelected || isProcessing}
                                onClick={goSignup}
                                className={`py-2 xl:py-4 px-8 xl:px-28 font-notojp font-bold text-sm xl:text-base rounded-3xl whitespace-nowrap
                                        ${
                                            !isAllSelected || isProcessing
                                                ? 'text-gray-300 cursor-not-allowed'
                                                : 'text-gray-500 hover:opacity-90'
                                        }`}
                            >
                                アカウントを作成して次へ
                            </button>
                        </div>
                        <div className="flex justify-center w-full sm:w-[40%] xl:w-[18%] px-2 lx:px-4">
                            <button
                                disabled={!isAllSelected || isProcessing}
                                onClick={goLogin}
                                className={`m-2 py-4 px-12 bg-color-base-2 font-notojp font-bold text-color-white-base rounded-3xl shadow-right-bottom hover:opacity-90 whitespace-nowrap
                                        ${
                                            !isAllSelected || isProcessing
                                                ? 'bg-gray-300 cursor-not-allowed'
                                                : 'bg-color-base-2 hover:opacity-90'
                                        }`}
                            >
                                ログインして次へ
                            </button>
                        </div>
                    </>
                )}
                <div className="flex flex-grow"></div>
            </div>
        </>
    );
}
